import { BookingLayout } from "app/core/layouts/BookingLayout"
import { Button } from "app/core/components/Button"
import { z } from "zod"
import Spinner from "react-md-spinner"

import style from "app/bookings/bookings.module.css"
import "app/core/styles/fontstyles.module.css"
import React, { useEffect, useRef, useState } from "react"
import assert from "utils/assert"
import { invoke, useMutation } from "@blitzjs/rpc"
import subscribeToNewsletter from "app/certificate-generator/mutations/subscribeToNewsletter"
import Form from "app/core/components/Form"
import LabeledTextField from "app/core/components/LabeledTextField"
import trackEvent from "app/userlist/mutations/trackEvent"

const IDCard = ({ item: { id, type }, setItems }) => {
  const [imageSrc, setImageSrc] = useState<string | undefined>()
  const fileRef = useRef<HTMLInputElement>(null)
  const elRef = useRef<HTMLDivElement>(null)

  const handleDelete = () => {
    setItems((prevItems: any) => {
      return prevItems.filter((item: any) => item.id !== id)
    })
  }

  const handleUpload = () => {
    assert(fileRef.current, "No file ref yet")
    assert(fileRef.current.files, "No file uploaded yet")
    const imageSrc = URL.createObjectURL(fileRef.current.files[0])
    setImageSrc(imageSrc)
  }
  const imageClass = imageSrc ? "id-card__image-bg" : "hidden"
  const imageStyle = imageSrc ? { backgroundImage: `url(${imageSrc})` } : {}

  useEffect(() => {
    let timeoutId
    if (elRef.current && id > 1) {
      const top = elRef.current.getBoundingClientRect().top
      timeoutId = setTimeout(() => {
        window.scrollTo({
          top,
          behavior: "smooth",
        })
      }, 10)
    }
    return () => clearTimeout(timeoutId)
  }, [id])

  const certificateImg =
    type === "educator" ? "/images/educator-certificate.png" : "/images/student-id-certificate.png"
  const contactPlaceholder = type === "educator" ? "CONTACT INFO" : "PARENT CONTACT INFO"

  return (
    <div className="print-section" ref={elRef}>
      <div className="card-container">
        <img src={certificateImg} className="block" alt="Student ID Certificate" />
        <div
          className={imageClass}
          style={imageStyle}
          onClick={() => fileRef.current && fileRef.current.click()}
          title="Click to change photo"
          role="button"
          tabIndex={0}
          onKeyPress={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              fileRef.current && fileRef.current.click()
            }
          }}
        />
        <input type="file" ref={fileRef} className="hidden" onChange={handleUpload} />
        {fileRef.current ? (
          ""
        ) : (
          <button
            className="id-card__image-input dont-print"
            onClick={() => fileRef.current && fileRef.current.click()}
          >
            Upload Photo
          </button>
        )}

        <input
          type="text"
          placeholder="FIRST NAME"
          className="id-card__text-input id-card__text-input__one"
        />
        <input
          type="text"
          placeholder="LAST NAME"
          className="id-card__text-input id-card__text-input__one-two"
        />
        <input
          type="text"
          placeholder="SCHOOL NAME"
          className="id-card__text-input id-card__text-input__three"
        />
        <input
          type="text"
          placeholder="GRADE"
          className="id-card__text-input id-card__text-input__two"
        />
        <input
          type="text"
          placeholder={contactPlaceholder}
          className="id-card__text-input id-card__text-input__four"
        />
      </div>
      <Button className="bg-red-600 text-lg py-3 px-6 mt-4 dont-print" onClick={handleDelete}>
        Remove
      </Button>
    </div>
  )
}

const NewsletterInput = z.object({
  email: z.string().email(),
})
const NewsletterSignup = ({ onSuccess }) => {
  const [subscribeToNewsletterMutation, { isPending: isLoading }] = useMutation(
    subscribeToNewsletter
  )
  const handleSubscribe = async ({ email }) => {
    try {
      await subscribeToNewsletterMutation({ email })
      onSuccess(email)
      localStorage.setItem("email", email)
    } catch (error) {
      console.error(error)
    }
  }
  return (
    <Form onSubmit={handleSubscribe} schema={NewsletterInput}>
      <div className="flex items-end">
        <LabeledTextField
          name="email"
          label="Email"
          placeholder="Email"
          autoComplete="email"
          type="email"
        />
        <div>
          <div />
          <Button type="submit" className="ml-2">
            {isLoading ? (
              <div className="flex justify-center">
                <Spinner size={20} singleColor="#ffffff" />
              </div>
            ) : (
              <span>Subscribe</span>
            )}
          </Button>
        </div>
      </div>
    </Form>
  )
}

const IdGenerator = () => {
  const [email, setEmail] = useState<string | null>(null)
  const [id, setId] = useState(1)
  const [items, setItems] = useState<any>([])

  const addNew = (type: string) => {
    setItems((prevItems) => [...prevItems, { id, type }])
    setId(id + 1)
  }
  const handlePrint = async () => {
    window.print()

    await invoke(trackEvent, {
      name: "id_card_printed",
      email,
    })
  }

  useEffect(() => {
    const email = localStorage.getItem("email")
    if (email) {
      setEmail(email)
    }
  }, [])

  useEffect(() => {
    if (document) {
      if (email) {
        document.body.classList.add("certificate-generator-page")
        document.body.classList.remove("certificate-generator-page--disabled")
      } else {
        document.body.classList.add("certificate-generator-page--disabled")
      }
    }
  }, [email])

  return (
    <div className="flex flex-col lg:flex-row">
      <div className="mb-16 lg:w-full">
        <div className="max-w-xs mx-auto">
          <div className="mb-8">
            <h3 className="mb-2 text-lg">
              <span className="font-bold">1.</span> Add Multiple Cards & Enter Info
            </h3>
            <form>
              <fieldset className="certificate-generator__fieldset">
                <div className="mr-4 mb-1 flex items-center">
                  <Button type="button" onClick={() => addNew("student")}>
                    Add Student ID Card
                  </Button>
                </div>
                <div className="mr-4 mb-1 flex items-center">
                  <Button type="button" onClick={() => addNew("educator")}>
                    Add Educator ID Card
                  </Button>
                </div>
              </fieldset>
            </form>
          </div>
          {email ? (
            <div className="mb-8">
              <h3 className="mb-2 text-lg">
                <span className="font-bold">2.</span> Thanks for joining our newsletter!
              </h3>
            </div>
          ) : (
            <div className="mb-8">
              <h3 className="mb-2 text-lg">
                <span className="font-bold">2.</span> Sign up for our newsletter
              </h3>
              <NewsletterSignup onSuccess={setEmail} />
            </div>
          )}
          <div>
            <h3 className={email ? "mb-2 text-lg" : "mb-2 opacity-40 text-lg"}>
              <span className="font-bold">3.</span> Print when you&apos;re all done!
            </h3>
            <fieldset disabled={!email}>
              {email ? (
                <Button onClick={handlePrint}>Print Certificates</Button>
              ) : (
                <span className="opacity-40">
                  Sign up to our newsletter to print your certificates.
                </span>
              )}
            </fieldset>
          </div>
        </div>
      </div>
      <div className="print-container">
        {items.length > 0 ? (
          items.map((item) => <IDCard key={item.id} item={item} setItems={setItems} />)
        ) : (
          <div className="text-center text-xl font-semibold lg:mx-12 p-12 rounded border border-gray-200 dont-print">
            Click one of card types on the left
          </div>
        )}
      </div>
    </div>
  )
}

IdGenerator.getLayout = (page) => (
  <BookingLayout title="Free Homeschool ID Cards" subtitle="Free Homeschool ID Cards">
    <div className={style["bookings-container"]}>{page}</div>
  </BookingLayout>
)

export default IdGenerator
